import { Atom, EllipsisVertical, Check } from 'lucide-react'
import { useSnapshot } from 'valtio'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import GradientText from '@/components/animate/GradientText'
import { chatState } from '@/store'
import { cn } from '@/utils/css'

// 深度思考模式枚举
export enum DeepThinkMode {
  AUTO = 'auto',
  ON = 'on',
  OFF = 'off',
}

// 深度思考选项配置
const deepThinkOptions = [
  {
    value: DeepThinkMode.AUTO,
    label: '自动',
    description: '根据问题决定智能切换',
  },
  {
    value: DeepThinkMode.ON,
    label: '开启',
    description: '输出带推理过程的答案',
  },
  {
    value: DeepThinkMode.OFF,
    label: '关闭',
    description: '快速直接回答',
  },
]

const DeepThink = () => {
  const { deepThinkMode } = useSnapshot(chatState)

  const handleModeChange = (mode: DeepThinkMode) => {
    chatState.deepThinkMode = mode
    localStorage.setItem('chat-taichu-deepThinkMode', mode)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              'relative flex justify-center items-center gap-1 px-2 py-1 rounded-full transition-colors text-xs border h-[28px] w-[130px] cursor-pointer',
              deepThinkMode === DeepThinkMode.AUTO
                ? 'border-none'
                : deepThinkMode === DeepThinkMode.ON
                  ? 'text-primary border-primary bg-primary/5'
                  : 'border-[#1C1F231A] text-content-second hover:border-primary/50',
            )}
          >
            {deepThinkMode === DeepThinkMode.AUTO && (
              <span
                className={cn(
                  'absolute inset-0 block h-full w-full animate-gradient rounded-[inherit] bg-gradient-to-r from-[#C17BFF]/50 via-[#004FF8]/50 to-[#C17BFF]/50  bg-[length:300%_100%] p-[1px]',
                )}
                style={{
                  WebkitMask:
                    'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'destination-out',
                  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  maskComposite: 'subtract',
                  WebkitClipPath: 'padding-box',
                }}
              />
            )}
            <Atom
              className={
                deepThinkMode === DeepThinkMode.ON
                  ? 'text-[#0077FACC]'
                  : 'text-content-second'
              }
              size={16}
            />
            <span
              className={cn(
                deepThinkMode === DeepThinkMode.OFF
                  ? 'text-content-second'
                  : 'text-[#0077FACC]',
                'whitespace-nowrap',
              )}
            >
              {deepThinkMode === DeepThinkMode.AUTO ? (
                <GradientText
                  colors={['#004FF8', '#C17BFF', '#004FF8']}
                  animationSpeed={5}
                  showBorder={false}
                  className="text-xs"
                >
                  深度思考·自动
                </GradientText>
              ) : deepThinkMode === DeepThinkMode.OFF ? (
                '深度思考·关闭'
              ) : (
                '深度思考·开启'
              )}
            </span>
            <EllipsisVertical
              size={12}
              className={
                deepThinkMode === DeepThinkMode.OFF
                  ? 'text-content-second'
                  : 'text-[#0077FACC]'
              }
            />
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-56 p-1" sideOffset={8}>
          {deepThinkOptions.map(option => (
            <DropdownMenuItem
              key={option.value}
              className="flex flex-col items-start gap-1 px-3 py-[6px] cursor-pointer hover:bg-brandgray-normal focus:bg-brandgray-normal rounded-none border-b border-content-first/5 last-of-type:border-none"
              onSelect={() => {
                handleModeChange(option.value)
              }}
            >
              <div className="text-sm flex gap-2 items-center">
                {option.label}
                {deepThinkMode === option.value && <Check size={12} />}
              </div>
              <div className="text-xs text-content-second">
                {option.description}
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

export default DeepThink
